import type { Location } from '../models/PreparationModels';
import type { PreparationLineWithProduct } from '../hooks/usePreparationQuery';

export interface ResolvedPickingBatch {
  batchId: number;
  batch?: {
    id?: number;
    batch_number?: string | null;
    expiry_date?: string | null;
  };
}

/** El GET /preparations/:id envía el lote como `batch: { id, batch_number, ... }` (no siempre `batch_id` en la raíz). */
function batchIdFromLocation(loc: Location | null | undefined): number | undefined {
  if (!loc) return undefined;
  if (loc.batch_id != null && Number(loc.batch_id) > 0) {
    return Number(loc.batch_id);
  }
  if (loc.batch?.id != null && Number(loc.batch.id) > 0) {
    return Number(loc.batch.id);
  }
  return undefined;
}

function batchMetaFromLocation(loc: Location): ResolvedPickingBatch['batch'] | undefined {
  const b = loc.batch;
  if (!b) return undefined;
  return {
    id: b.id,
    batch_number: b.batch_number ?? undefined,
    expiry_date: b.expiry_date ?? undefined,
  };
}

/** Lote a usar al picar: viene de la ubicación escaneada (línea de preparación / stock_batches). */
export function resolvePickingBatchAtLocation(
  line: PreparationLineWithProduct,
  scannedLocation: Location | null | undefined,
): ResolvedPickingBatch | null {
  if (!scannedLocation?.code?.trim()) return null;

  const fromScan = batchIdFromLocation(scannedLocation);
  if (fromScan != null) {
    return { batchId: fromScan, batch: batchMetaFromLocation(scannedLocation) };
  }

  const code = scannedLocation.code.trim();
  const entries = [...(line.suggested_locations ?? []), ...(line.other_locations ?? [])];
  const match = entries.find((l) => l.code?.trim() === code);
  if (!match) return null;

  const fromMatch = batchIdFromLocation(match);
  if (fromMatch != null) {
    return { batchId: fromMatch, batch: batchMetaFromLocation(match) };
  }

  const batches = Array.isArray(match.stock_batches) ? match.stock_batches : [];
  const forProduct = batches.filter((s) => s.product_id == null || s.product_id === line.product_id);
  if (forProduct.length === 1) {
    const s = forProduct[0];
    return { batchId: s.batch_id, batch: s.batch ?? undefined };
  }
  if (forProduct.length > 1) {
    const best = [...forProduct].sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0))[0];
    return { batchId: best.batch_id, batch: best.batch ?? undefined };
  }

  return null;
}
